import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPen, faTrash, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { AdminNavComponent } from '../../core/components/admin-nav/admin-nav.component';
import { EquipmentModalComponent } from '../../core/components/equipment-modal/equipment-modal.component';
import { ConfirmModalComponent } from '../../core/components/confirm-modal/confirm-modal.component';
import { SessionService } from '../../core/services/session.service';
import { AdminEquipmentService, EquipmentPayload } from '../../core/services/admin-equipment.service';
import { Equipment } from '../../core/services/equipment.service';

/** Admin CRUD over the public product catalogue (/equipment). Admin-only. */
@Component({
    selector: 'app-admin-equipment',
    imports: [CommonModule, FormsModule, FontAwesomeModule, MenuBarComponent, FooterComponent, AdminNavComponent],
    templateUrl: './admin-equipment.component.html',
    styleUrl: './admin-equipment.component.scss',
})
export class AdminEquipmentComponent implements OnInit {
    equipment: Equipment[] = [];
    loading = false;

    /** Frontend-only search over the loaded rows. */
    search = '';

    /**
     * Per-thumbnail load tracking, as on the public catalogue: the list hands
     * back a signed S3 URL per row, all fetched after the table has rendered.
     * Keyed by id and never cleared, so filtering cannot re-flash a loaded row.
     */
    private readonly settled = new Set<string>();
    private readonly failedImages = new Set<string>();

    readonly faPen = faPen;
    readonly faTrash = faTrash;
    readonly faUpRightFromSquare = faUpRightFromSquare;

    constructor(
        private equipmentService: AdminEquipmentService,
        private session: SessionService,
        private router: Router,
        private modalService: NgbModal,
        private toastr: ToastrService,
    ) { }

    ngOnInit(): void {
        // The route guard enforces this too; this is the in-component backstop.
        if (!this.session.isLoggedIn() || !this.session.isAdmin()) {
            this.router.navigate(['/']);
            return;
        }
        this.load();
    }

    get filtered(): Equipment[] {
        const q = this.search.trim().toLowerCase();
        if (!q) return this.equipment;
        return this.equipment.filter((e) =>
            (e.code || '').toLowerCase().includes(q) ||
            (e.name || '').toLowerCase().includes(q) ||
            (e.slug || '').toLowerCase().includes(q));
    }

    load(): void {
        this.loading = true;
        // a re-fetch hands back freshly signed URLs, so the rows load again
        this.settled.clear();
        this.failedImages.clear();
        this.equipmentService.list().subscribe({
            next: (res) => {
                this.equipment = res?.equipment ?? [];
                this.loading = false;
            },
            error: () => {
                this.equipment = [];
                this.loading = false;
                this.toastr.error('Unable to load the equipment catalogue');
            },
        });
    }

    openAdd(): void {
        const ref = this.modalService.open(EquipmentModalComponent, {
            centered: true,
            size: 'lg',
            windowClass: 'auth-modal-window',
        });
        ref.result.then(
            (payload: EquipmentPayload) => {
                if (!payload) return;
                this.equipmentService.create(payload).subscribe({
                    next: () => {
                        this.toastr.success(`${payload.code} created`);
                        this.load();
                    },
                    // the backend rejects a duplicate code or slug — surface why
                    error: (err) => this.toastr.error(this.messageFor(err, 'Unable to create this product')),
                });
            },
            () => { /* dismissed */ },
        );
    }

    openEdit(item: Equipment): void {
        const ref = this.modalService.open(EquipmentModalComponent, {
            centered: true,
            size: 'lg',
            windowClass: 'auth-modal-window',
        });
        ref.componentInstance.equipment = item;
        ref.result.then(
            (payload: EquipmentPayload) => {
                if (!payload) return;
                this.equipmentService.update(item.id, payload).subscribe({
                    next: () => {
                        this.toastr.success(`${payload.code} updated`);
                        this.load();
                    },
                    error: (err) => this.toastr.error(this.messageFor(err, 'Unable to update this product')),
                });
            },
            () => { /* dismissed */ },
        );
    }

    remove(item: Equipment): void {
        const ref = this.modalService.open(ConfirmModalComponent, {
            centered: true,
            windowClass: 'auth-modal-window',
        });
        ref.componentInstance.title = 'Delete equipment';
        ref.componentInstance.message = 'This permanently removes the product from the catalogue.';
        ref.componentInstance.subject = item.name ? `${item.code} — ${item.name}` : item.code;
        ref.componentInstance.consequence = `Its page at /equipment/${item.slug} will stop working.`;
        ref.componentInstance.confirmLabel = 'Delete';
        ref.result.then(
            () => {
                this.equipmentService.delete(item.id).subscribe({
                    next: () => {
                        this.toastr.success(`${item.code} deleted`);
                        this.load();
                    },
                    error: (err) => this.toastr.error(this.messageFor(err, 'Unable to delete this product')),
                });
            },
            () => { /* dismissed */ },
        );
    }

    /** Opens the live catalogue page, so an admin can check how it renders. */
    viewPublic(item: Equipment): void {
        window.open(`/equipment/${encodeURIComponent(item.slug)}`, '_blank');
    }

    specCount(item: Equipment): number {
        return item.specifications?.length ?? 0;
    }

    /** Whether to show the skeleton in this row's thumbnail slot. */
    imagePending(item: Equipment): boolean {
        return !!item.image && !this.settled.has(item.id);
    }

    /** A thumbnail that 404s or expires falls back to the "no image" dash. */
    imageBroken(item: Equipment): boolean {
        return this.failedImages.has(item.id);
    }

    onImageLoad(item: Equipment): void {
        this.settled.add(item.id);
    }

    onImageError(item: Equipment): void {
        this.settled.add(item.id);
        this.failedImages.add(item.id);
    }

    /** HttpServiceBase.handleError rethrows { httpError, service }. */
    private messageFor(err: any, fallback: string): string {
        return err?.httpError?.error?.message || err?.error?.message || fallback;
    }
}
